import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Heart, Minus, Plus, Truck, ShieldCheck, Gem, Play } from "lucide-react";
import { toast } from "sonner";
import { getProductBySlug, formatINR, products } from "@/data/products";
import ProductCard from "@/components/product/ProductCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCart } from "@/context/CartContext";
import VideoPlayer from "@/components/product/VideoPlayer";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const related = useMemo(() => {
    if (!product) return [];
    // Related: same category, prefer same collection
    return products
      .filter((p) => p.id !== product.id && p.category === product.category)
      .sort((a, b) => (b.collection === product.collection ? 1 : 0) - (a.collection === product.collection ? 1 : 0))
      .slice(0, 4);
  }, [product]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const items = [];
    if (product.images.lifestyle) items.push({ type: "image", src: product.images.lifestyle, alt: `${product.name} — worn` });
    if (product.images.closeUp) items.push({ type: "image", src: product.images.closeUp, alt: `${product.name} — detail` });
    if (product.videoId) items.push({ type: "video", id: product.videoId });
    if (product.images.hero) items.push({ type: "image", src: product.images.hero, alt: `${product.name} — hero` });
    if (product.images.plain) items.push({ type: "image", src: product.images.plain, alt: `${product.name}` });
    return items;
  }, [product]);

  const [activeIdx, setActiveIdx] = useState(0);

  if (!product) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-32 text-center">
        <p className="overline">404</p>
        <h1 className="font-serif text-5xl mt-4 text-[#2A2626]">This piece has drifted away.</h1>
        <Link
          to="/shop"
          className="inline-block mt-10 bg-[#2A2626] text-[#FAF8F5] px-8 py-3 text-[11px] uppercase tracking-[0.24em]"
        >
          Back to shop
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    addItem(
      {
        id: product.id,
        slug: product.slug,
        name: product.name,
        price: product.price,
        image: product.thumbnail || product.images.plain || product.images.hero,
      },
      qty
    );
    toast.success(`${product.name} added to your bag`);
  };

  const activeItem = gallery[activeIdx] || gallery[0];

  return (
    <div data-testid="product-detail-page" className="bg-[#FAF8F5]">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pt-6 md:pt-8">
        <nav className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
          <Link to="/" className="hover:text-[#2A2626]">Home</Link>
          <ChevronRight size={12} strokeWidth={1.5} />
          <Link to={`/shop/${product.category.toLowerCase()}`} className="hover:text-[#2A2626]">
            {product.category}
          </Link>
          <ChevronRight size={12} strokeWidth={1.5} />
          <span className="text-[#2A2626] truncate">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-[64px_1fr] md:grid-cols-[80px_1fr] gap-3 md:gap-4">
              {/* Thumbs */}
              <div className="flex flex-col gap-2 md:gap-3">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActiveIdx(i)}
                    className={`relative aspect-square bg-[#F0EBE1] overflow-hidden border transition-colors ${
                      activeIdx === i ? "border-[#2A2626]" : "border-transparent hover:border-[#DCD5CA]"
                    }`}
                    aria-label={`Show image ${i + 1}`}
                  >
                    {g.type === "video" ? (
                      <div className="w-full h-full flex items-center justify-center bg-[#2A2626] text-[#FAF8F5]">
                        <Play size={18} strokeWidth={1.5} />
                      </div>
                    ) : (
                      <img src={g.src} alt="" className="w-full h-full object-cover" />
                    )}
                  </button>
                ))}
              </div>
              {/* Main media */}
              <div className="relative aspect-[4/5] bg-[#F0EBE1] overflow-hidden">
                {activeItem?.type === "video" ? (
                  <VideoPlayer videoId={activeItem.id} className="absolute inset-0" />
                ) : (
                  <img
                    src={activeItem?.src}
                    alt={activeItem?.alt || product.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 lg:pl-4">
            <div className="lg:sticky lg:top-28">
              <p className="overline">
                {product.category === "Pendants" ? "The Pendant Line" : `${product.collection} Collection`}
              </p>
              <h1
                data-testid="product-name"
                className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#2A2626]"
              >
                {product.name}
              </h1>
              <p
                data-testid="product-price"
                className="mt-5 md:mt-6 font-sans text-2xl text-[#2A2626]"
              >
                {formatINR(product.price)}
              </p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
                Inclusive of all taxes
              </p>

              <div className="mt-6 md:mt-8 flex flex-wrap gap-2">
                {product.gemstones.map((g) => (
                  <span
                    key={g}
                    data-testid={`gemstone-tag-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="px-3 py-1.5 border border-[#DCD5CA] text-[11px] uppercase tracking-[0.2em] text-[#2A2626] bg-[#FAF8F5]"
                  >
                    {g}
                  </span>
                ))}
              </div>

              <div className="mt-8 md:mt-10">
                <p className="overline mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-[#DCD5CA]">
                    <button
                      data-testid="qty-decrease"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="p-3 hover:bg-[#F0EBE1]"
                      aria-label="Decrease"
                    >
                      <Minus size={14} strokeWidth={1.5} />
                    </button>
                    <span className="px-5 font-sans text-sm">{qty}</span>
                    <button
                      data-testid="qty-increase"
                      onClick={() => setQty((q) => q + 1)}
                      className="p-3 hover:bg-[#F0EBE1]"
                      aria-label="Increase"
                    >
                      <Plus size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="add-to-cart-button"
                  onClick={handleAdd}
                  className="flex-1 bg-[#2A2626] text-[#FAF8F5] px-6 md:px-8 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
                >
                  Add to bag · {formatINR(product.price * qty)}
                </button>
                <button
                  data-testid="wishlist-button"
                  onClick={() => toast.success("Saved to wishlist")}
                  className="sm:w-14 flex items-center justify-center border border-[#2A2626] text-[#2A2626] hover:bg-[#2A2626] hover:text-[#FAF8F5] transition-colors py-4 px-6"
                  aria-label="Add to wishlist"
                >
                  <Heart strokeWidth={1.5} size={18} />
                </button>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 md:gap-4 pt-8 border-t border-[#DCD5CA]">
                {[
                  { Icon: Truck, label: "Free shipping", sub: "Over ₹4,000" },
                  { Icon: Gem, label: "Handcrafted", sub: "One at a time" },
                  { Icon: ShieldCheck, label: "6-month care", sub: "Support included" },
                ].map(({ Icon, label, sub }) => (
                  <div key={label} className="text-center">
                    <Icon strokeWidth={1.25} size={20} className="mx-auto text-[#2A2626]" />
                    <p className="mt-2 font-serif text-sm text-[#2A2626]">{label}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#5C564F] mt-0.5">
                      {sub}
                    </p>
                  </div>
                ))}
              </div>

              <Accordion type="single" collapsible defaultValue="desc" className="mt-8 md:mt-10">
                <AccordionItem value="desc" className="border-t border-[#DCD5CA]">
                  <AccordionTrigger
                    data-testid="accordion-description"
                    className="text-[11px] uppercase tracking-[0.22em] text-[#2A2626] py-5 hover:no-underline"
                  >
                    Description
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[#5C564F] pb-6 whitespace-pre-line">
                    {product.description}
                  </AccordionContent>
                </AccordionItem>
                {product.gemstoneStory && (
                  <AccordionItem value="stone" className="border-t border-[#DCD5CA]">
                    <AccordionTrigger className="text-[11px] uppercase tracking-[0.22em] text-[#2A2626] py-5 hover:no-underline">
                      Gemstone Story
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-[#5C564F] pb-6 whitespace-pre-line">
                      {product.gemstoneStory}
                    </AccordionContent>
                  </AccordionItem>
                )}
                {product.materials && (
                  <AccordionItem value="materials" className="border-t border-[#DCD5CA]">
                    <AccordionTrigger className="text-[11px] uppercase tracking-[0.22em] text-[#2A2626] py-5 hover:no-underline">
                      Materials &amp; Craft
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] leading-relaxed text-[#5C564F] pb-6">
                      <ul className="list-disc pl-5 space-y-1.5">
                        {product.materials.split(",").map((m) => (
                          <li key={m}>{m.trim()}</li>
                        ))}
                      </ul>
                      {product.silverDetails && (
                        <p className="mt-4 italic">{product.silverDetails}</p>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )}
                <AccordionItem value="care" className="border-t border-b border-[#DCD5CA]">
                  <AccordionTrigger className="text-[11px] uppercase tracking-[0.22em] text-[#2A2626] py-5 hover:no-underline">
                    Care &amp; Shipping
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[#5C564F] pb-6">
                    Store your Innayaa piece in a soft pouch, away from moisture and
                    direct perfume. Ships within 2–3 working days across India in our
                    signature keepsake box.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      {/* Editorial video block (mobile-friendly) — for products with video */}
      {product.videoId && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center border-t border-[#DCD5CA] pt-12 md:pt-20">
            <div>
              <p className="overline">In Motion</p>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-[#2A2626]">
                See it move.
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[#5C564F] max-w-md">
                Watch the {product.name} in motion — the way the stones catch light
                and the chain flows gently with every turn.
              </p>
            </div>
            <VideoPlayer
              videoId={product.videoId}
              className="aspect-[9/16] max-w-[380px] mx-auto md:mx-0 md:ml-auto"
            />
          </div>
        </section>
      )}

      {/* Related */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 lg:px-16 py-16 md:py-24 lg:py-28 border-t border-[#DCD5CA]">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <p className="overline">Pairs beautifully with</p>
            <h2 className="mt-3 font-serif text-2xl sm:text-3xl md:text-4xl tracking-tight text-[#2A2626]">
              You may also love
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-8 md:gap-y-16">
          {related.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
